
import React, { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AddressSectionProps } from './types';
import AddressAutocomplete from './AddressAutocomplete'; // Corrected casing
import { GeoLocation } from '@/components/business/onboarding/types/locationTypes';
import { useToast } from '@/components/ui/use-toast';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { usStates } from '@/utils/stateOptions';
import { timezoneOptions } from '@/utils/timezoneOptions';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const AddressSection: React.FC<AddressSectionProps> = ({
  businessDetails,
  handleInputChange,
  handleSelectChange
}) => {
  // State for zip code validation
  const [zipError, setZipError] = React.useState<string | null>(null);
  const { toast } = useToast();
  
  // Track whether timezone was manually selected
  const [timezoneManuallySelected, setTimezoneManuallySelected] = React.useState(false);
  
  // Validate zip code on change
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const zipValue = e.target.value;
    
    // Allow only numbers and limit to 5 digits
    if (zipValue && !/^\d{0,5}$/.test(zipValue)) {
      return;
    }
    
    handleInputChange(e);
    
    if (zipValue && zipValue.length > 0 && zipValue.length < 5) {
      setZipError("ZIP code must be 5 digits");
    } else {
      setZipError(null);
    }
  };

  // Handle timezone selection - mark as manually selected
  const handleTimezoneChange = (value: string) => {
    setTimezoneManuallySelected(true);
    handleSelectChange(value, 'timeZone');
  };

  // Handle address selection from autocomplete
  const handleAddressSelected = (addressData: Partial<typeof businessDetails>) => {
    console.log("Address selected from autocomplete:", addressData);
    
    // Process state field first to ensure it's set correctly
    if (addressData.state) {
      console.log("Setting state value:", addressData.state);
      handleSelectChange(addressData.state as string, 'state');
    }
    
    // Process all other fields
    Object.entries(addressData).forEach(([key, value]) => {
      if (key !== 'state' && key !== 'coordinates' && key !== 'timeZone') {
        const e = {
          target: {
            name: key,
            value: value
          }
        } as React.ChangeEvent<HTMLInputElement>;
        handleInputChange(e);
      }
    });
    
    // Handle coordinates separately if needed in parent component
    if (addressData.coordinates && 
        typeof addressData.coordinates.latitude === 'number' && 
        typeof addressData.coordinates.longitude === 'number' && 
        businessDetails.handleCoordinatesUpdate) {
      businessDetails.handleCoordinatesUpdate({
        latitude: addressData.coordinates.latitude, // Changed from .lat
        longitude: addressData.coordinates.longitude // Changed from .lng
      });
    }

    // Set default timezone based on state if a state was selected and timezone wasn't manually set
    if (addressData.state && !timezoneManuallySelected) {
      const stateCode = addressData.state as string;
      let defaultTimezone = '';
      
      // Map state to default timezone
      switch(stateCode) {
        case 'ME': case 'NH': case 'VT': case 'MA': case 'RI': case 'CT': 
        case 'NY': case 'NJ': case 'DE': case 'MD': case 'DC': case 'VA': 
        case 'NC': case 'SC': case 'GA': case 'FL': case 'PA': case 'WV':
        case 'OH': // Moved OH to Eastern
          defaultTimezone = 'Eastern Standard Time';
          break;
        case 'MI': case 'IN': case 'KY': case 'TN': case 'AL': // OH removed from Central
        case 'MS': case 'IL': case 'MO': case 'AR': case 'LA': case 'WI': 
        case 'MN': case 'IA':
          defaultTimezone = 'Central Standard Time';
          break;
        case 'ND': case 'SD': case 'NE': case 'KS': case 'OK': 
        case 'TX': case 'MT': case 'WY': case 'CO': case 'NM':
          defaultTimezone = 'Mountain Standard Time';
          break;
        case 'AZ':
          defaultTimezone = 'Arizona Standard Time';
          break;
        case 'ID': case 'UT': case 'NV': case 'CA': case 'OR': case 'WA':
          defaultTimezone = 'Pacific Standard Time';
          break;
        case 'AK':
          defaultTimezone = 'Alaska Standard Time';
          break;
        case 'HI':
          defaultTimezone = 'Hawaii Standard Time';
          break;
        case 'PR': case 'VI':
          defaultTimezone = 'Puerto Rico and US Virgin Islands Time';
          break;
        case 'GU': case 'MP':
          defaultTimezone = 'Chamorro Standard Time';
          break;
        case 'AS':
          defaultTimezone = 'Samoa Standard Time';
          break;
        default:
          defaultTimezone = 'Eastern Standard Time'; // Default
      }
      
      // Set the timezone directly
      handleSelectChange(defaultTimezone, 'timeZone');
      
      // Show a toast notification about the timezone update
      toast({
        title: "Timezone updated",
        description: `Timezone set to ${defaultTimezone} based on the selected state.`,
      });
    }
  };

  // Preserve manual timezone selection when state changes. When the user
  // manually selects a state, we simply update the state field. Timezone
  // auto-population only occurs when an address is chosen from Google Maps
  // via handleAddressSelected.
  const handleStateChange = (value: string) => {
    handleSelectChange(value, 'state');
  };

  // Dummy function to satisfy the component interface
  const ensureTimeZone = async (): Promise<boolean> => {
    // Since we're using dropdown selection, we don't need to auto-detect
    return true;
  };

  // Expose the ensureTimeZone function to the parent component
  useEffect(() => {
    if (businessDetails.handleEnsureTimeZone) {
      businessDetails.handleEnsureTimeZone = ensureTimeZone;
    }
  }, []);

  return (
    <div className="space-y-6">
      <AddressAutocomplete
        businessDetails={businessDetails}
        onAddressSelected={handleAddressSelected}
        onManualAddressChange={handleInputChange}
      />
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="space-y-2 col-span-1">
          <label htmlFor="city" className="text-sm font-medium">City</label>
          <Input
            id="city"
            name="city"
            value={businessDetails.city}
            onChange={handleInputChange}
            placeholder="City"
          />
        </div>
        
        <div className="space-y-2 col-span-1">
          <label htmlFor="state" className="text-sm font-medium">State</label>
          <Select
            value={businessDetails.state}
            onValueChange={handleStateChange}
          >
            <SelectTrigger id="state" className="w-full">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent 
              className="bg-white max-h-[200px] overflow-y-auto scrollbar-thin" 
              position="popper"
            >
              {usStates.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="hidden md:block space-y-2 col-span-1">
          <label htmlFor="zip-desktop" className="text-sm font-medium">ZIP Code</label>
          <Input
            id="zip-desktop"
            name="zip"
            value={businessDetails.zip}
            onChange={handleZipChange}
            placeholder="ZIP"
            maxLength={5}
          />
          {zipError && <p className="text-xs text-red-500">{zipError}</p>}
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full md:hidden flex space-x-4">
          <div className="w-1/3 space-y-2">
            <label htmlFor="zip-mobile" className="text-sm font-medium">ZIP Code</label>
            <Input
              id="zip-mobile"
              name="zip"
              value={businessDetails.zip}
              onChange={handleZipChange}
              placeholder="ZIP"
              maxLength={5}
            />
            {zipError && <p className="text-xs text-red-500">{zipError}</p>}
          </div>
          
          <div className="w-2/3 space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="timeZone-mobile" className="text-sm font-medium">Time Zone</label>
            </div>
            <Select
              value={businessDetails.timeZone}
              onValueChange={handleTimezoneChange}
            >
              <SelectTrigger id="timeZone-mobile" className="w-full">
                <SelectValue placeholder="Select time zone" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[200px] overflow-y-auto scrollbar-thin">
                {timezoneOptions.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="hidden md:block space-y-2 w-full">
          <div className="flex items-center justify-between">
            <label htmlFor="timeZone" className="text-sm font-medium">Time Zone</label>
          </div>
          <Select
            value={businessDetails.timeZone}
            onValueChange={handleTimezoneChange}
          >
            <SelectTrigger id="timeZone" className="w-full">
              <SelectValue placeholder="Select time zone" />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-[200px] overflow-y-auto scrollbar-thin">
              {timezoneOptions.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            Select your business's time zone
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddressSection;
